import { Injectable } from '@angular/core';
import * as logRocket from 'logrocket';

import { AccountProfileService } from '../lib/accountProfile.service';
import { FirebaseFunctionsService } from './firebaseFunctions.service';
import { FirestorePathsService } from './firestorePaths.service';
import { DealershipService } from '../lib/dealership.service';
import { MapHelperService } from '../lib/mapHelper.service';
import { SessionService } from '../lib/session.service';
import { SettingsService } from '../lib/settings.service';
import { SmsService } from '../lib/sms.service';
import { UserAccountMapsService } from '../lib/userAccountMaps.service';
import { UsersService } from '../lib/users.service';
import { UtilsService } from '../lib/utils.service';

import type {
    TAccountProfile,
    TDealership,
    TSetting,
    TUser,
    TUserAccountMap
} from '@shared/models';
import { Constants } from '@shared/models';
@Injectable({
    'providedIn': 'root'
})
export class LoaderService {
    constructor(private firestorePathsService: FirestorePathsService, private usersService: UsersService,
        private sessionService: SessionService, private userAccountMapsService: UserAccountMapsService,
        private accountProfileService: AccountProfileService, private utilsService: UtilsService,
        private mapHelperService: MapHelperService, private dealershipService: DealershipService,
        private settingsService: SettingsService, private smsService: SmsService,
        private firebaseFunctionsService: FirebaseFunctionsService) {
    }

    // public async prepareSessionOLD(userUid: string) {
    //     try {
    //         const map: TUserAccountMap = await this.userAccountMapsService.getByUid(userUid);
    //         const accountNumber = map.accountNumber;
    //         // Most of the firebase nodes are referenced off the account number.
    //         // So, now we can initialize those nodes.
    //         this.firestorePathsService.setAccount(accountNumber);

    //         const user: TUser = await this.usersService.getByUid(userUid);
    //         this.sessionService.setCurrentUser(user);

    //         const profile: TAccountProfile = await this.accountProfileService.get();
    //         this.sessionService.setAccountProfile(profile);

    //         const dealership: TDealership = await this.dealershipService.get();
    //         this.sessionService.setDealership(dealership);

    //         // const areSettingsComplete = await this.menuService.areSettingsComplete();
    //         // await this.shuttleMovementService.initialize();

    //         // Hold these objects in sessionService so the rest of the application can get whatever it needs.
    //         this.sessionService.reloadSettings();

    //         //
    //         const domainInfo = this.utilsService.getDomainInfo();
    //         this.sessionService.setDomainInfo(domainInfo);

    //         this.mapHelperService.init();
    //         const onDevice = this.mapHelperService.runningOnDevice();
    //         this.sessionService.setIsDesktopDevice(!onDevice);
    //         this.sessionService.setIsMobileDevice(onDevice);

    //         // Initialize logRocket.
    //         const appID = this.utilsService.getLogRocketAppID();
    //         //            // eslint-disable-next-line import/namespace
    //         logRocket.init(appID, {
    //             release: Constants.APPLICATION_VERSION
    //         });
    //         //            // eslint-disable-next-line import/namespace
    //         logRocket.identify(userUid, {
    //             'name': user.firstName + ' ' + user.lastName,
    //             'email': user.email,
    //             'accountNumber': accountNumber,
    //         });

    //         // Creating the session needs to be the last thing we do in this long list.
    //         // const session = this.sessionService.getSessionData();
    //         // await this.sessionsService.addUniqueSession(session);

    //         // await this.tasksService.initialize();
    //         // await this.funnelrService.addUser(user, dealership, profile, map.pwd);

    //         // const userguid = '44D1A0CA-B6F4-48B6-9633-FB46FE1C98C9';
    //         // const userId   = 31;
    //         // let tagNames = await this.funnelrService.getUserTags(userId);
    //         // console.log('tagNames = ', tagNames );

    //         // const tagNamesToRemove = ['Customer', 'Prospect' ];
    //         // await this.funnelrService.removeTagsFromUser(tagNamesToRemove, userId);

    //         // tagNames = await this.funnelrService.getUserTags(userId);
    //         // console.log('tagNames = ', tagNames );
    //         // } catch (err) {
    //         //     throw err;
    //     } finally {
    //         //
    //     }
    // }

    public async prepareSession(userUid: string) {
        let accountNumber = '';
        try {
            const map: TUserAccountMap = await this.getUserAccountMap(userUid);
            accountNumber = map.accountNumber;

            // Most of the firebase nodes are referenced off the account number.
            // So, now we can initialize those nodes.
            this.firestorePathsService.setAccount(accountNumber);

            // Recalculate the account status, in case anything has changed since the last time they logged in.
            await this.accountProfileService.recalculateStatus(accountNumber);

            const user: TUser = await this.getUser(userUid);
            this.sessionService.setCurrentUser(user);

            const profile: TAccountProfile = await this.getAccountProfile();
            this.sessionService.setAccountProfile(profile);

            const dealership: TDealership = await this.getDealership();
            this.sessionService.setDealership(dealership);

            // const areSettingsComplete = await this.menuService.areSettingsComplete();
            // await this.shuttleMovementService.initialize();

            // Hold these objects in sessionService so the rest of the application can get whatever it needs.
            // this.sessionService.reloadSettings();
            
            //
            const domainInfo = this.utilsService.getDomainInfo();
            this.sessionService.setDomainInfo(domainInfo);

            this.mapHelperService.init();
            const onDevice = this.mapHelperService.runningOnDevice();
            this.sessionService.setIsDesktopDevice(!onDevice);
            this.sessionService.setIsMobileDevice(onDevice);

            const settings: TSetting =  await this.getSettings();
            // this.sessionService.setSetting(settings);
            this.smsService.initialize(settings);

            // Compute the rewards for this account, in case anything has changed since the last time they logged in.
            // Non-critical: a failure here should not block the login flow.
            try {
                await this.firebaseFunctionsService.evaluateRewardFunnelRules(accountNumber);
            } catch (rewardErr) {
                console.warn('evaluateRewardFunnelRules failed (non-critical):', rewardErr);
            }

            // Initialize logRocket.
            const appID = this.utilsService.getLogRocketAppID();
            //            // eslint-disable-next-line import/namespace
            logRocket.init(appID, {
                release: Constants.APPLICATION_VERSION
            });
            //            // eslint-disable-next-line import/namespace
            logRocket.identify(userUid, {
                'name': user.firstName + ' ' + user.lastName,
                'email': user.email,
                'accountNumber': accountNumber,
            });

            // Creating the session needs to be the last thing we do in this long list.
            // const session = this.sessionService.getSessionData();
            // await this.sessionsService.addUniqueSession(session);

            // await this.tasksService.initialize();
            // await this.funnelrService.addUser(user, dealership, profile, map.pwd);

            // const userguid = '44D1A0CA-B6F4-48B6-9633-FB46FE1C98C9';
            // const userId   = 31;
            // let tagNames = await this.funnelrService.getUserTags(userId);
            // console.log('tagNames = ', tagNames );

            // const tagNamesToRemove = ['Customer', 'Prospect' ];
            // await this.funnelrService.removeTagsFromUser(tagNamesToRemove, userId);

            // tagNames = await this.funnelrService.getUserTags(userId);
            // console.log('tagNames = ', tagNames );
            // } catch (err) {
            //     throw err;
        } catch (err) {
            const errorMessage = this.utilsService.extractError(err);
            throw new Error(`Loader failed for account: ${accountNumber}. ${errorMessage}`);
        }
    }

    private getUserAccountMap(userUid: string): Promise<TUserAccountMap> {
        try {
            return this.userAccountMapsService.getByUid(userUid);
        } catch (err) {
            throw new Error(`Unable to get user account map. {{userUid}}`);
        }
    }

    private getUser(userUid: string): Promise<TUser> {
        try {
            return this.usersService.getByUid(userUid);
        } catch (err) {
            throw new Error(`Unable to get user. {{userUid}}`);
        }
    }

    private getAccountProfile(): Promise<TAccountProfile> {
        try {
            return this.accountProfileService.get();
        } catch (err) {
            throw new Error('Unable to get account profile.');
        }
    }

    private getDealership(): Promise<TDealership> {
        try {
            return this.dealershipService.get();
        } catch (err) {
            throw new Error('Unable to get dealership.');
        }
    }

    private async getSettings(): Promise<TSetting> {
        try {
            return await this.settingsService.get();
        } catch (err) {
            throw new Error('Unable to get settings.');
        }
    }
}
